import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, ArrowUpDown, ExternalLink, Layers, MoreHorizontal, Search, Trash2 } from "lucide-react";
import AppShell from "@/components/AppShell";
import NewProjectForm from "@/components/NewProjectForm";
import PropertyProgressPanel from "@/components/PropertyProgressPanel";
import {
  Banner,
  Button,
  ConfirmDialog,
  EmptyState,
  Panel,
  Spinner,
  StatTile,
  StatusBadge,
} from "@/components/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  deleteProject,
  downloadShapefile,
  errorDetail,
  getProjectStats,
  listBlocks,
  listProjects,
  listWards,
  listZones,
} from "@/lib/api";
import type { BlockStatus, Project } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/i18n";
import { formatDate, formatNumber } from "@/lib/format";
import { getStoredUser } from "@/lib/auth";

type Rollup = {
  zones: number;
  wards: number;
  blocks: number;
  byStatus: Record<BlockStatus, number>;
};

const EMPTY_ROLLUP: Rollup = {
  zones: 0,
  wards: 0,
  blocks: 0,
  byStatus: { pending: 0, in_progress: 0, submitted: 0 },
};

/* Block-state colours, the same three the map's "Highlight progress" uses, so
   a block reads the same here and there. Not started is the neutral track. */
const STATE_FILL: Record<BlockStatus, string> = {
  submitted: "bg-status-good",
  in_progress: "bg-status-warning",
  pending: "bg-line-strong",
};

type SortKey = "name" | "progress" | "created";

/** "<1%" rather than "0%" when there is some, so a start never reads as none. */
const sharePct = (part: number, whole: number) => {
  if (!whole || !part) return "0%";
  const p = (part / whole) * 100;
  return p < 1 ? "<1%" : `${Math.round(p)}%`;
};

const pctOf = (r: Rollup | undefined) =>
  r && r.blocks > 0 ? Math.round((r.byStatus.submitted / r.blocks) * 100) : 0;

export default function ProjectsPage() {
  const { t, lang } = useLanguage();
  const tp = t.projectsPage;
  const isStaff = (getStoredUser()?.role ?? "surveyor") !== "surveyor";
  const [projects, setProjects] = useState<Project[]>([]);
  // Was a window.confirm. The most destructive action in the portal, so it
  // gets the same dialog as everything else rather than a native prompt.
  const [confirmingDelete, setConfirmingDelete] = useState<Project | null>(null);
  /** Set once this project's shapes have actually downloaded, which is what
   *  unlocks the delete. Not persisted: reopening the dialog exports again, so
   *  the file always matches what is about to be destroyed. */
  const [exportedForDelete, setExportedForDelete] = useState(false);
  const [exportingFor, setExportingFor] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [deletingId, setDeletingId] = useState<number | null>(null);
  /** Null keeps the server's order (newest first). */
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" } | null>(null);

  /**
   * Downloads the project's collected shapes, and only then unlocks Delete.
   *
   * Required rather than offered: deleting a project destroys every shape
   * surveyors collected in it, and a shape someone walked to cannot be
   * recreated from anything the office holds. The same gate guards the
   * hierarchy clear, for the same reason.
   */
  async function exportBeforeDelete(project: Project) {
    setExportingFor(true);
    setError(null);
    try {
      await downloadShapefile({ projectId: project.id });
      setExportedForDelete(true);
    } catch (err) {
      // A project with nothing collected yet 404s here. That is not a failure
      // to guard against -- there is no field work to lose, so the gate opens.
      const text = errorDetail(err);
      if (/nothing to export/i.test(text)) {
        setExportedForDelete(true);
      } else {
        setError(text);
      }
    } finally {
      setExportingFor(false);
    }
  }

  async function handleDelete(project: Project) {
    setConfirmingDelete(null);

    setDeletingId(project.id);
    setError(null);
    try {
      await deleteProject(project.id);
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : tp.couldntDeleteProject);
    } finally {
      setDeletingId(null);
    }
  }

  // Per-project rollups, cached so switching the selected row is instant.
  const [statsCache, setStatsCache] = useState<Record<number, Rollup>>({});
  const statsCacheRef = useRef<Record<number, Rollup>>({});
  statsCacheRef.current = statsCache;

  const [rollupFor, setRollupFor] = useState<number | null>(null);
  const [rollup, setRollup] = useState<Rollup>(EMPTY_ROLLUP);
  const [rollupLoadedOnce, setRollupLoadedOnce] = useState(false);

  // Fetch stats for a project, caching the result
  const fetchStatsForProject = useCallback(async (projectId: number): Promise<Rollup> => {
    if (statsCacheRef.current[projectId]) {
      return statsCacheRef.current[projectId];
    }
    try {
      // 1. Try fast backend stats endpoint (single query ~100ms)
      const s = await getProjectStats(projectId);
      const res: Rollup = {
        zones: s.zones,
        wards: s.wards,
        blocks: s.blocks,
        byStatus: {
          pending: s.by_status?.pending ?? 0,
          in_progress: s.by_status?.in_progress ?? 0,
          submitted: s.by_status?.submitted ?? 0,
        },
      };
      setStatsCache((prev) => ({ ...prev, [projectId]: res }));
      return res;
    } catch {
      // 2. Fallback to walking hierarchy if needed
      try {
        const zones = await listZones(projectId);
        const wardLists = await Promise.all(zones.map((z) => listWards(z.id)));
        const wards = wardLists.flat();
        const blockLists = await Promise.all(wards.map((w) => listBlocks(w.id)));
        const blocks = blockLists.flat();
        const byStatus: Record<BlockStatus, number> = {
          pending: 0,
          in_progress: 0,
          submitted: 0,
        };
        for (const b of blocks) {
          if (b.status in byStatus) byStatus[b.status] += 1;
        }
        const res: Rollup = {
          zones: zones.length,
          wards: wards.length,
          blocks: blocks.length,
          byStatus,
        };
        setStatsCache((prev) => ({ ...prev, [projectId]: res }));
        return res;
      } catch {
        return EMPTY_ROLLUP;
      }
    }
  }, []);

  // On load: select the first project, and prefetch every project's stats --
  // the table's progress column and the portfolio overview need all of them.
  useEffect(() => {
    listProjects()
      .then((p) => {
        setProjects(p);
        if (p.length > 0) {
          setRollupFor(p[0].id);
          fetchStatsForProject(p[0].id).then((st) => {
            setRollup(st);
            setRollupLoadedOnce(true);
          });
          for (let i = 1; i < p.length; i++) {
            fetchStatsForProject(p[i].id);
          }
        }
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : tp.couldntReachBackend)
      )
      .finally(() => setLoading(false));
  }, [fetchStatsForProject]);

  // Selecting a row: instant from the cache, else one fetch.
  const handleProjectSelect = useCallback(
    (projectId: number) => {
      setRollupFor(projectId);
      const cached = statsCacheRef.current[projectId];
      if (cached) {
        setRollup(cached);
        setRollupLoadedOnce(true);
      } else {
        fetchStatsForProject(projectId).then((st) => {
          setRollup(st);
          setRollupLoadedOnce(true);
        });
      }
    },
    [fetchStatsForProject]
  );

  const visibleProjects = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = q
      ? projects.filter(
          (p) =>
            p.name.toLowerCase().includes(q) ||
            (p.description ?? "").toLowerCase().includes(q)
        )
      : [...projects];
    if (!sort) return list;
    const dir = sort.dir === "asc" ? 1 : -1;
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
    return list.sort((a, b) => {
      if (sort.key === "name") return dir * collator.compare(a.name, b.name);
      if (sort.key === "created")
        return dir * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      return dir * (pctOf(statsCache[a.id]) - pctOf(statsCache[b.id]));
    });
  }, [projects, filter, sort, statsCache]);

  // Portfolio totals: every project, not just the selected one. "—" until all
  // rollups are in, so a total never shows a partial sum as if it were final.
  const portfolio = useMemo(() => {
    const all = projects.map((p) => statsCache[p.id]);
    if (projects.length === 0 || all.some((r) => !r)) return null;
    const sum = (f: (r: Rollup) => number) => all.reduce((n, r) => n + f(r!), 0);
    return {
      zones: sum((r) => r.zones),
      wards: sum((r) => r.wards),
      blocks: sum((r) => r.blocks),
      submitted: sum((r) => r.byStatus.submitted),
      inProgress: sum((r) => r.byStatus.in_progress),
      pending: sum((r) => r.byStatus.pending),
      started: all.filter((r) => r!.byStatus.submitted + r!.byStatus.in_progress > 0).length,
    };
  }, [projects, statsCache]);

  const activeProject = projects.find((p) => p.id === rollupFor) ?? null;
  const completion = pctOf(rollup);

  const status = (r: Rollup | undefined) => {
    if (!r) return null;
    if (r.blocks === 0) return { tone: "neutral" as const, label: tp.statusNoBlocks };
    if (r.byStatus.submitted === r.blocks) return { tone: "good" as const, label: tp.statusComplete };
    if (r.byStatus.submitted + r.byStatus.in_progress > 0)
      return { tone: "warning" as const, label: tp.statusInProgress };
    return { tone: "neutral" as const, label: tp.statusNotStarted };
  };

  // Click 1: the natural direction (A→Z, highest progress, newest). Click 2:
  // reversed. Click 3: back to the server's order.
  const toggleSort = (key: SortKey) =>
    setSort((s) => {
      const first = key === "name" ? "asc" : "desc";
      if (s?.key !== key) return { key, dir: first };
      if (s.dir === first) return { key, dir: first === "asc" ? "desc" : "asc" };
      return null;
    });

  // A render helper, not a component: a component declared in here is a new
  // type every render, so React would remount the header button on each click
  // and throw away its keyboard focus.
  const sortHeader = (k: SortKey, label: string, className?: string) => {
    const on = sort?.key === k;
    const Icon = !on ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
    return (
      <th
        className={cn("px-4 py-2.5 font-semibold", className)}
        aria-sort={on ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
      >
        <button
          type="button"
          onClick={() => toggleSort(k)}
          title={tp.sortBy(label)}
          className={cn(
            "inline-flex items-center gap-1 uppercase tracking-wider transition hover:text-ink",
            on && "text-ink"
          )}
        >
          {label}
          <Icon className={cn("h-3 w-3", !on && "opacity-40")} aria-hidden="true" />
        </button>
      </th>
    );
  };

  const legend = (
    <ul className="hidden items-center gap-3 text-2xs text-ink-secondary md:flex" aria-label={tp.colProgress}>
      {(
        [
          ["submitted", tp.legendSubmitted],
          ["in_progress", tp.legendInProgress],
          ["pending", tp.legendNotStarted],
        ] as const
      ).map(([k, label]) => (
        <li key={k} className="flex items-center gap-1.5">
          <span className={cn("h-2 w-2 rounded-sm", STATE_FILL[k])} aria-hidden="true" />
          {label}
        </li>
      ))}
    </ul>
  );

  return (
    <AppShell crumbs={[{ label: t.sidebar.projects }]}>
      <div className="mx-auto max-w-[1400px] space-y-5 p-5">
        {/* ── Page header ─────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-ink">{tp.heading}</h1>
            <p className="mt-0.5 text-xs text-ink-muted">{tp.subheading}</p>
          </div>
          {isStaff && (
            <NewProjectForm
              onCreated={(p) => {
                setProjects((prev) => [p, ...prev]);
                setRollupFor(p.id);
                setStatsCache((prev) => ({ ...prev, [p.id]: EMPTY_ROLLUP }));
                setRollup(EMPTY_ROLLUP);
                setRollupLoadedOnce(true);
              }}
            />
          )}
        </div>

        {error && (
          <Banner
            tone="danger"
            action={<Button size="sm" onClick={() => location.reload()}>{t.common.retry}</Button>}
          >
            {error}
          </Banner>
        )}

        {/* ── Portfolio overview: totals across every project ─────────── */}
        {projects.length > 0 && (
          <section aria-label={tp.overview}>
            <h2 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-ink-muted">
              {tp.overview}
            </h2>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatTile className="self-stretch"
                label={tp.kpiProjects}
                value={formatNumber(projects.length, lang)}
                hint={portfolio ? tp.kpiProjectsHint(portfolio.started) : "—"}
              />
              <StatTile className="self-stretch"
                label={tp.kpiBlocks}
                value={portfolio ? formatNumber(portfolio.blocks, lang) : "—"}
                hint={portfolio ? tp.kpiBlocksHint(portfolio.zones, portfolio.wards) : "—"}
              />
              <StatTile className="self-stretch"
                label={tp.kpiSubmitted}
                tone="good"
                value={portfolio ? formatNumber(portfolio.submitted, lang) : "—"}
                hint={portfolio ? tp.kpiSubmittedHint(sharePct(portfolio.submitted, portfolio.blocks)) : "—"}
              />
              <StatTile className="self-stretch"
                label={tp.kpiInProgress}
                tone="warning"
                value={portfolio ? formatNumber(portfolio.inProgress, lang) : "—"}
                hint={portfolio ? tp.kpiInProgressHint(portfolio.pending) : "—"}
              />
            </div>
          </section>
        )}

        {/* ── Project list ────────────────────────────────────────────── */}
        <Panel
          title={tp.allProjects(projects.length)}
          actions={
            <>
              {legend}
              <label className="relative">
                <span className="sr-only">{tp.searchLabel}</span>
                <Search
                  className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-muted"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder={tp.filterPlaceholder}
                  className="h-8 w-56 rounded border-0 bg-surface-sunken pl-7 pr-2.5 text-xs text-ink shadow-ring placeholder:text-ink-muted focus:bg-surface-raised"
                />
              </label>
            </>
          }
        >
          {loading ? (
            <div className="flex items-center justify-center gap-2 px-4 py-12 text-sm text-ink-muted">
              <Spinner /> {tp.loadingProjects}
            </div>
          ) : visibleProjects.length === 0 ? (
            <EmptyState
              title={projects.length === 0 ? tp.noProjectsYet : tp.noMatches}
              body={projects.length === 0 ? tp.noProjectsBody : tp.noMatchesBody}
            />
          ) : (
            <div className="thin-scroll overflow-x-auto overflow-y-hidden">
              <table className="w-full min-w-[56rem] border-collapse text-left text-xs">
                <thead className="bg-surface-sunken text-2xs uppercase tracking-wider text-ink-muted">
                  <tr>
                    {sortHeader("name", tp.colProject)}
                    <th className="px-4 py-2.5 font-semibold">{tp.colStatus}</th>
                    <th className="px-4 py-2.5 font-semibold">{tp.colHierarchy}</th>
                    {sortHeader("progress", tp.colProgress)}
                    {sortHeader("created", tp.colCreated)}
                    <th className="px-4 py-2.5 text-right font-semibold">
                      <span className="sr-only">{tp.colActions}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleProjects.map((project) => {
                    const s = statsCache[project.id];
                    const isSelected = project.id === rollupFor;
                    const pct = pctOf(s);
                    const st = status(s);
                    return (
                      <tr
                        key={project.id}
                        onClick={() => handleProjectSelect(project.id)}
                        onKeyDown={(e) => {
                          if (e.target !== e.currentTarget) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleProjectSelect(project.id);
                          }
                        }}
                        tabIndex={0}
                        aria-selected={isSelected}
                        className={cn(
                          "cursor-pointer border-t border-line outline-none transition-colors focus-visible:bg-surface-sunken",
                          isSelected
                            ? "bg-accent-subtle/35 shadow-[inset_3px_0_0_#0077B6]"
                            : "hover:bg-surface-sunken/60"
                        )}
                      >
                        {/* Project: name + description, one column instead of two */}
                        <td className="max-w-[22rem] px-4 py-3">
                          <Link
                            to={`/projects/${project.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-sm font-semibold text-ink hover:text-accent-solid hover:underline"
                          >
                            {project.name}
                          </Link>
                          {project.description && (
                            <p className="mt-0.5 truncate text-2xs text-ink-muted" title={project.description}>
                              {project.description}
                            </p>
                          )}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3">
                          {st ? (
                            <StatusBadge tone={st.tone} label={st.label} />
                          ) : (
                            <span className="inline-block h-3 w-20 animate-pulse rounded bg-surface-sunken" />
                          )}
                        </td>

                        <td className="tabular whitespace-nowrap px-4 py-3 text-ink-secondary">
                          {s ? (
                            <>
                              {formatNumber(s.zones, lang)} · {formatNumber(s.wards, lang)} ·{" "}
                              <span className="font-semibold text-ink">{formatNumber(s.blocks, lang)}</span>
                            </>
                          ) : (
                            "—"
                          )}
                        </td>

                        {/* Stacked by block state; the legend is in the header. */}
                        <td className="px-4 py-3">
                          {s ? (
                            <div className="w-52">
                              <div
                                className="flex h-2 w-full gap-[2px] overflow-hidden rounded-full bg-surface-sunken"
                                role="img"
                                aria-label={`${tp.legendSubmitted} ${s.byStatus.submitted}, ${tp.legendInProgress} ${s.byStatus.in_progress}, ${tp.legendNotStarted} ${s.byStatus.pending}`}
                                title={`${tp.legendSubmitted} ${s.byStatus.submitted} · ${tp.legendInProgress} ${s.byStatus.in_progress} · ${tp.legendNotStarted} ${s.byStatus.pending}`}
                              >
                                {s.blocks > 0 &&
                                  (["submitted", "in_progress", "pending"] as const).map(
                                    (k) =>
                                      s.byStatus[k] > 0 && (
                                        <span
                                          key={k}
                                          className={cn("h-full first:rounded-l-full last:rounded-r-full", STATE_FILL[k])}
                                          style={{ width: `${(s.byStatus[k] / s.blocks) * 100}%` }}
                                        />
                                      )
                                  )}
                              </div>
                              <p className="tabular mt-1 text-2xs text-ink-muted">
                                {s.blocks > 0
                                  ? tp.blocksProgress(s.byStatus.submitted, s.blocks, pct)
                                  : tp.zeroBlocks}
                              </p>
                            </div>
                          ) : (
                            <div className="h-2 w-52 animate-pulse rounded bg-surface-sunken" />
                          )}
                        </td>

                        <td className="tabular whitespace-nowrap px-4 py-3 text-ink-secondary">
                          {formatDate(project.created_at, lang)}
                        </td>

                        {/* One visible action; the rest behind ⋯ so Delete is
                            never a single mis-click away on every row. */}
                        <td
                          className="whitespace-nowrap px-4 py-3 text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="inline-flex items-center gap-1">
                            <Link to={`/projects/${project.id}`}>
                              <Button size="sm" variant="secondary">
                                {tp.open}
                              </Button>
                            </Link>
                            {isStaff && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button
                                    type="button"
                                    aria-label={tp.moreActions(project.name)}
                                    title={tp.moreActions(project.name)}
                                    disabled={deletingId === project.id}
                                    className="flex h-8 w-8 items-center justify-center rounded text-ink-muted transition hover:bg-surface-sunken hover:text-ink disabled:opacity-40"
                                  >
                                    {deletingId === project.id ? (
                                      <Spinner className="h-3.5 w-3.5" />
                                    ) : (
                                      <MoreHorizontal className="h-4 w-4" />
                                    )}
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-52">
                                  <DropdownMenuItem asChild>
                                    <Link to={`/projects/${project.id}`}>
                                      <ExternalLink className="h-3.5 w-3.5 text-ink-muted" />
                                      {tp.openWorkspace}
                                    </Link>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem asChild>
                                    <Link to={`/projects/${project.id}/hierarchy`}>
                                      <Layers className="h-3.5 w-3.5 text-ink-muted" />
                                      {tp.hierarchyAction}
                                    </Link>
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-status-text-critical focus:text-status-text-critical"
                                    onSelect={() => {
                                      setExportedForDelete(false);
                                      setConfirmingDelete(project);
                                    }}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                    {tp.deleteAction}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        {/* ── Selected project: its figures and property progress ─────── */}
        {activeProject && (
          <section aria-label={tp.selectedProject} className="space-y-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="text-2xs font-semibold uppercase tracking-wider text-ink-muted">
                  {tp.selectedProject}
                </p>
                <h2 className="mt-0.5 text-lg font-semibold text-ink">{activeProject.name}</h2>
                <p className="text-2xs text-ink-muted">{tp.selectHint}</p>
              </div>
              <div className="flex gap-2">
                {isStaff && (
                  <Link to={`/projects/${activeProject.id}/hierarchy`}>
                    <Button size="sm" variant="secondary">
                      {tp.hierarchyAction}
                    </Button>
                  </Link>
                )}
                <Link to={`/projects/${activeProject.id}`}>
                  <Button size="sm" variant="primary">
                    {tp.openWorkspace}
                  </Button>
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatTile className="self-stretch" label={tp.zones} value={rollupLoadedOnce ? rollup.zones : "—"} />
              <StatTile className="self-stretch" label={tp.wards} value={rollupLoadedOnce ? rollup.wards : "—"} />
              <StatTile className="self-stretch" label={tp.blocks} value={rollupLoadedOnce ? rollup.blocks : "—"} />
              <StatTile className="self-stretch"
                label={tp.complete}
                value={rollupLoadedOnce ? `${completion}%` : "—"}
                tone={completion === 100 && rollup.blocks > 0 ? "good" : "accent"}
                hint={
                  rollupLoadedOnce ? tp.submittedOfTotal(rollup.byStatus.submitted, rollup.blocks) : "—"
                }
              />
            </div>

            <PropertyProgressPanel projectId={activeProject.id} caption={activeProject.name} />
          </section>
        )}
      </div>

      <ConfirmDialog
        open={confirmingDelete !== null}
        title={tp.deleteProjectTitle}
        body={
          <>
            <span className="font-medium text-ink">{confirmingDelete?.name}</span>
            {tp.deleteProjectBodySuffix}
          </>
        }
        confirmLabel={tp.deleteProjectConfirmLabel}
        confirmDisabled={!exportedForDelete}
        onCancel={() => setConfirmingDelete(null)}
        onConfirm={() => confirmingDelete && void handleDelete(confirmingDelete)}
      >
        <div className="rounded bg-surface-sunken p-2.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-2xs text-ink-secondary">
              {exportedForDelete ? tp.exportedCanDelete : tp.exportRequired}
            </span>
            <Button
              size="sm"
              variant={exportedForDelete ? "secondary" : "primary"}
              onClick={() => confirmingDelete && void exportBeforeDelete(confirmingDelete)}
              disabled={exportingFor}
            >
              {exportingFor ? <Spinner className="h-3 w-3" /> : null}
              {exportingFor
                ? t.common.preparing
                : exportedForDelete
                  ? t.common.exportAgain
                  : t.common.exportShapefile}
            </Button>
          </div>
        </div>
      </ConfirmDialog>
    </AppShell>
  );
}
